import {
  AppPlayLinksProps,
  ArticleCardCarousel,
  BulletedKeyValueList,
  CarouselInlineCaption,
  CategoryCarousel,
  CategoryExplore,
  CopyColumn,
  DetailPageOverview,
  EditorialFeature,
  EditorialFullBleed,
  EditorialIconBlocks,
  EmailSignUp,
  Hero,
  Quote,
  TwoUp,
  VimeoPlayer,
} from "@bluebottlecoffee/design-system";
import { toArticleCardCarouselProps } from "./transformers/article-card-carousel";
import { toCarouselInlineCaptionProps } from "./transformers/carousel-inline-caption";
import { toCategoryCarouselProps } from "./transformers/category-carousel";
import { toCategoryExploreProps } from "./transformers/category-explore";
import { toCopyColumnProps } from "./transformers/copy-column";
import { toEditorialFeatureProps } from "./transformers/editorial-feature";
import { toEditorialFullBleedProps } from "./transformers/editorial-full-bleed";
import { toEditorialIconBlocksProps } from "./transformers/editorial-icon-blocks";
import { toHeroProps } from "./transformers/hero";
import { toQuoteProps } from "./transformers/quote";
import { toTwoUpProps } from "./transformers/two-up";
import { toBrewGuideDetailPageOverviewProps } from "./transformers/detail-page-overview";
import { toVimeoPlayerProps } from "./transformers/vimeo-player";
import { toBulletedKeyValueListProps } from "./transformers/bulleted-key-value-list";
import { ElementOf } from "./utils";
import {
  BrewGuide,
  BrewGuidesPage,
  Cafe,
  HelpPage,
  Homepage,
  InstantEspresso,
  OurCoffee,
  OurStory,
  OurValues,
  Page,
  Product,
  ShopCollection,
  SubscriptionPage,
  Sustainability,
} from "./sanity-schema";
import { CafeSearch } from "./algolia/instant-search/CafeSearch";
import {
  DereferencedCafeLocator,
  toCafeSearchModuleProps,
} from "./transformers/cafe-locator";
import { DereferencedCafesPage } from "../pages/[region]/[lang]/cafes";
import {
  toCopyObject,
  TranslationGroupSanityType,
} from "./sanity/translation-group-queries";
import { toEmailSignUpProps } from "./transformers/email-sign-up";

type ModuleElementType =
  | ElementOf<BrewGuide["modules"]>
  | ElementOf<BrewGuidesPage["modules"]>
  | ElementOf<Cafe["modules"]>
  | ElementOf<DereferencedCafesPage["modules"]>
  | ElementOf<HelpPage["modules"]>
  | ElementOf<Homepage["modules"]>
  | ElementOf<InstantEspresso["modules"]>
  | ElementOf<OurCoffee["modules"]>
  | ElementOf<OurStory["modules"]>
  | ElementOf<OurValues["modules"]>
  | ElementOf<Page["modules"]>
  | ElementOf<Product["modules"]>
  | ElementOf<ShopCollection["modules"]>
  | ElementOf<SubscriptionPage["modules"]>
  | ElementOf<Sustainability["modules"]>;

export default function renderModule(
  module: ModuleElementType,
  region: string,
  lang: string,
  appPlayLinks?: TranslationGroupSanityType,
  isDark?: boolean,
) {
  try {
    switch (module._type) {
      case "articleCardCarousel": {
        return (
          <ArticleCardCarousel
            {...toArticleCardCarouselProps(module, region, lang)}
          />
        );
      }
      case "bulletedKeyValueList": {
        return (
          <BulletedKeyValueList
            {...toBulletedKeyValueListProps(module, "cream", region, lang)}
          />
        );
      }
      case "carouselInlineCaption": {
        return (
          <CarouselInlineCaption
            {...toCarouselInlineCaptionProps(module, region, lang)}
          />
        );
      }
      case "cafeLocator": {
        return (
          <CafeSearch
            {...toCafeSearchModuleProps({
              data: module as DereferencedCafeLocator,
              lang,
              region,
            })}
          />
        );
      }
      case "categoryCarousel": {
        return (
          <CategoryCarousel
            {...toCategoryCarouselProps(module, region, lang, isDark)}
          />
        );
      }
      case "categoryExplore": {
        return (
          <CategoryExplore
            {...toCategoryExploreProps({ data: module, region, lang })}
          />
        );
      }
      case "copyColumnFreeFlow": {
        return <CopyColumn {...toCopyColumnProps(module, region, lang)} />;
      }
      case "detailPageOverview":
        return (
          <DetailPageOverview
            {...toBrewGuideDetailPageOverviewProps({
              data: module,
              region,
              lang,
            })}
            appPlayLinks={
              appPlayLinks
                ? toCopyObject<AppPlayLinksProps>(lang, appPlayLinks)
                : undefined
            }
          />
        );
      case "editorialFeature": {
        return (
          <EditorialFeature
            {...toEditorialFeatureProps(module, region, lang)}
          />
        );
      }
      case "editorialFullBleed": {
        return (
          <EditorialFullBleed
            {...toEditorialFullBleedProps(module, region, lang)}
          />
        );
      }
      case "editorialIconBlock": {
        return (
          <EditorialIconBlocks
            {...toEditorialIconBlocksProps({ data: module, lang })}
          />
        );
      }
      case "emailSignUp": {
        return <EmailSignUp {...toEmailSignUpProps({ data: module, lang })} />;
      }
      case "hero": {
        return <Hero {...toHeroProps(module, region, lang)} />;
      }
      case "quote":
        // passing null for optional positional argument topBorder
        return <Quote {...toQuoteProps(module, lang, null, isDark)} />;
      case "twoUp":
        return <TwoUp {...toTwoUpProps(module, region, lang, isDark)} />;
      case "vimeoPlayer":
        return <VimeoPlayer {...toVimeoPlayerProps(module)} />;
      default: {
        throw Error(`Module [${module._type}] did not have a generator.`);
      }
    }
  } catch (e) {
    console.debug(e);
    console.debug(module);
    throw new Error(
      `Rendering module ${module._type} failed with ${e.message},`,
    );
  }
}
